/**
 * ConnectionRenderer — Render connections on canvas
 * Per PLAN.md render/ConnectionRenderer.js and PRD §4.4
 */

import { ConnectionType } from '../models/Connection.js';
import { closestPointOnRectEdge, angle, calculateBezierControlPoints } from '../utils/geometry.js';
import { ARROW_SIZE } from '../utils/constants.js';

export class ConnectionRenderer {
    /**
     * Render all connections
     * @param {CanvasRenderingContext2D} ctx 
     * @param {Connection[]} connections 
     * @param {Map<string, Node>} nodes 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {string|null} selectedId - Currently selected connection ID
     * @param {boolean} isDark - Dark theme
     */
    render(ctx, connections, nodes, viewport, selectedId = null, isDark = false) {
        const strokeColor = isDark ? '#777777' : '#888888';
        const selectedColor = isDark ? '#3B82F6' : '#2563EB';

        for (const conn of connections) {
            const source = nodes.get(conn.sourceId);
            const target = nodes.get(conn.targetId);

            if (!source || !target) continue;

            const isSelected = conn.id === selectedId;
            this.renderConnection(ctx, conn, source, target, viewport, isSelected ? selectedColor : strokeColor, isSelected);
        }
    }

    /**
     * Render a single connection
     * @param {CanvasRenderingContext2D} ctx 
     * @param {Connection} conn 
     * @param {Node} source 
     * @param {Node} target 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {string} color 
     * @param {boolean} isSelected 
     */
    renderConnection(ctx, conn, source, target, viewport, color, isSelected) {
        const sourceBounds = source.getBounds();
        const targetBounds = target.getBounds();

        // Get edge points
        const sourceCenter = source.getCenter();
        const targetCenter = target.getCenter();

        const sourcePoint = closestPointOnRectEdge(targetCenter.x, targetCenter.y, sourceBounds);
        const targetPoint = closestPointOnRectEdge(sourceCenter.x, sourceCenter.y, targetBounds);

        // Convert to screen coordinates
        const start = viewport.worldToScreen(sourcePoint.x, sourcePoint.y);
        const end = viewport.worldToScreen(targetPoint.x, targetPoint.y);

        ctx.strokeStyle = color;
        ctx.lineWidth = isSelected ? 2.5 : 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Set dash pattern for dashed type
        if (conn.type === ConnectionType.DASHED) {
            ctx.setLineDash([8, 4]);
        } else {
            ctx.setLineDash([]);
        }

        ctx.beginPath();

        if (conn.curvature !== 0) {
            // Quadratic bezier for curved connections
            const cp = calculateBezierControlPoints(start.x, start.y, end.x, end.y, conn.curvature * viewport.zoom);
            ctx.moveTo(start.x, start.y);
            ctx.quadraticCurveTo(cp.cp1x, cp.cp1y, end.x, end.y);
        } else {
            // Straight line
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
        }

        ctx.stroke();
        ctx.setLineDash([]);

        // Draw arrows
        const arrowAngle = angle(start.x, start.y, end.x, end.y);
        const arrowSize = ARROW_SIZE * Math.max(0.5, viewport.zoom);

        if (conn.type === ConnectionType.ARROW || conn.type === ConnectionType.DASHED) {
            this.drawArrowHead(ctx, end.x, end.y, arrowAngle, arrowSize, color);
        } else if (conn.type === ConnectionType.DOUBLE_ARROW) {
            this.drawArrowHead(ctx, end.x, end.y, arrowAngle, arrowSize, color);
            this.drawArrowHead(ctx, start.x, start.y, arrowAngle + Math.PI, arrowSize, color);
        }
    }

    /**
     * Draw an arrow head
     * @param {CanvasRenderingContext2D} ctx 
     * @param {number} x 
     * @param {number} y 
     * @param {number} angle 
     * @param {number} size 
     * @param {string} color 
     */
    drawArrowHead(ctx, x, y, angle, size, color) {
        const angle1 = angle + Math.PI * 0.85;
        const angle2 = angle - Math.PI * 0.85;

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + Math.cos(angle1) * size, y + Math.sin(angle1) * size);
        ctx.lineTo(x + Math.cos(angle2) * size, y + Math.sin(angle2) * size);
        ctx.closePath();
        ctx.fill();
    }

    /**
     * Render connection being drawn
     * @param {CanvasRenderingContext2D} ctx 
     * @param {number} startX - Screen X
     * @param {number} startY - Screen Y
     * @param {number} endX - Screen X
     * @param {number} endY - Screen Y
     * @param {boolean} isDark 
     */
    renderPreview(ctx, startX, startY, endX, endY, isDark = false) {
        const color = isDark ? '#3B82F6' : '#2563EB';

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        ctx.setLineDash([]);

        // Arrow head
        const ang = angle(startX, startY, endX, endY);
        this.drawArrowHead(ctx, endX, endY, ang, ARROW_SIZE, color);
    }
}
